import React, { useContext, useEffect, useRef, useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { useParams } from "react-router-dom";
import { Context } from "../index";
import { fetchInitialMessagesAPI, indexServers, sendRcon } from "../http/ServerAPI";
import { observer } from "mobx-react-lite";
import { getApiErrorMessage } from "../http/apiError";
import ChatMessages from "./chat/components/ChatMessages";
import ChatInput from "./chat/components/ChatInput";
import ChatSidebarContent from "./chat/components/ChatSidebar";

const getPlayersLabel = (count) => {
    if (count % 10 === 1 && count % 100 !== 11) return `${count} игрок`;
    if (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 10 || count % 100 >= 20)) return `${count} игрока`;
    return `${count} игроков`;
};

const ChatPage = observer(() => {
    const { server_id } = useParams();
    const { user } = useContext(Context);
    const [name, setName] = useState('');
    const [chatMessages, setChatMessages] = useState([]);
    const [playerNames, setPlayerNames] = useState([]);
    const [message, setMessage] = useState("");
    const [playerCount, setPlayerCount] = useState(0);
    const [serverName, setServerName] = useState(server_id);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const chatEndRef = useRef(null);

    useEffect(() => {
        const indexServer = async () => {
            try {
                const data = await indexServers();
                user.setServers(data);
            } catch (e) {
                console.error(getApiErrorMessage(e, 'Ошибка получения данных сервера'));
            }
        };

        indexServer();
        const intervalId = setInterval(indexServer, 60000);
        return () => clearInterval(intervalId);
    }, [user]);

    useEffect(() => {
        if (user.userName) {
            setName(user.userName);
        }
    }, [user.userName]);

    useEffect(() => {
        if (user.servers.length && server_id) {
            const server = user.servers?.find(item => item.server_id === parseInt(server_id, 10));
            setServerName(server ? `${server.title} [${getPlayersLabel(server.online)}]` : server_id);
            setPlayerNames(server?.player_names ?? []);
            setPlayerCount(server?.online ?? 0);
        }
    }, [user.servers, server_id]);

    useEffect(() => {
        const loadInitialMessages = async () => {
            if (!server_id) return;

            setInitialLoading(true);
            try {
                const initialData = await fetchInitialMessagesAPI(server_id);
                setChatMessages(initialData.messages || []);
            } catch (err) {
                console.error(getApiErrorMessage(err, 'Ошибка загрузки начальных сообщений'));
                setChatMessages([]);
            } finally {
                setInitialLoading(false);
            }
        };

        loadInitialMessages();
        return () => {
            setChatMessages([]);
            setInitialLoading(true);
        };
    }, [server_id]);

    useEffect(() => {
        if (!server_id) return;

        const currentServerId = parseInt(server_id, 10);
        const chatData = user.getStorageByChatId(currentServerId);

        if (chatData && Array.isArray(chatData.data)) {
            const storeMessages = chatData.data;
            setChatMessages(prevMessages => {
                if (storeMessages.length <= prevMessages.length) return prevMessages;
                return [...prevMessages, ...storeMessages.slice(prevMessages.length)];
            });
        }
    }, [user.chat, server_id, user]);

    useEffect(() => {
        if (!initialLoading && chatMessages.length > 0 && chatEndRef.current) {
            requestAnimationFrame(() => {
                chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
            });
        }
    }, [chatMessages, initialLoading]);

    const handleSendMessage = async () => {
        const trimmedMessage = message.trim();

        if (!server_id) {
            alert("Ошибка: ID сервера не определен.");
            return;
        }

        if (!trimmedMessage) return;

        try {
            await sendRcon(server_id, trimmedMessage, 'chat');
            setChatMessages(prevMessages => [
                ...prevMessages,
                {
                    date: new Date().toISOString(),
                    sender: name,
                    chat_message: trimmedMessage,
                }
            ]);
            setMessage("");
        } catch (error) {
            alert(getApiErrorMessage(error, 'Ошибка отправки сообщения'));
        }
    };

    const toggleSidebar = (open) => () => setIsSidebarOpen(open);

    return (
        <Box sx={{ display: "flex", height: "calc(100vh - 70px)", overflow: "hidden" }}>
            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", bgcolor: "background.default" }}>
                <Box sx={{ display: { xs: "flex", md: "none" }, p: 1, bgcolor: "background.paper", justifyContent: "space-between", alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ ml: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {serverName}
                    </Typography>
                    <IconButton onClick={toggleSidebar(true)} aria-label="Открыть меню">
                        <MenuIcon />
                    </IconButton>
                </Box>

                <ChatMessages messages={chatMessages} loading={initialLoading} endRef={chatEndRef} />
                <ChatInput value={message} onChange={setMessage} onSend={handleSendMessage} disabled={initialLoading || !server_id} />
            </Box>

            <Box sx={{ display: { xs: "none", md: "flex" }, width: "250px", bgcolor: "background.paper", borderLeft: 1, borderColor: "divider", flexDirection: "column" }}>
                <ChatSidebarContent serverName={serverName} playerCount={playerCount} playerNames={playerNames} />
            </Box>

            <SwipeableDrawer
                anchor="right"
                open={isSidebarOpen}
                onClose={toggleSidebar(false)}
                onOpen={toggleSidebar(true)}
                sx={{ '& .MuiDrawer-paper': { width: '80%', maxWidth: 300 } }}
            >
                <ChatSidebarContent serverName={serverName} playerCount={playerCount} playerNames={playerNames} />
            </SwipeableDrawer>
        </Box>
    );
});

export default ChatPage;
